import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck, ArrowUpRight } from "lucide-react";
import { adminApi, auth, formatApiErrorDetail } from "@/lib/admin-auth";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  if (auth.isAuthed()) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Email and password are required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await adminApi.post("/admin/login", { email: email.trim(), password });
      auth.setToken(data.token);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || "Login failed.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="admin-login-page"
      className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6 bubaloo-grain"
    >
      <div className="w-full max-w-md">
        <a
          href="/"
          className="inline-flex items-center gap-3 mb-10"
          data-testid="admin-login-home"
        >
          <img
            src="/tbc-logo.jpeg"
            alt="The Bridge Collective"
            className="h-10 w-10 object-contain"
            style={{ filter: "invert(1) brightness(1.05) contrast(1.1)" }}
          />
          <div className="flex flex-col leading-tight">
            <span className="font-display tracking-[0.28em] text-white text-xs font-light">
              THE BRIDGE
            </span>
            <span className="font-display tracking-[0.28em] text-[#1A5C40] text-xs font-light">
              COLLECTIVE
            </span>
          </div>
        </a>

        <div className="border border-white/10 bg-[#0c0c0c] p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck size={16} className="text-[#1A5C40]" strokeWidth={1.5} />
            <span className="overline">Admin Console</span>
          </div>
          <h1 className="font-display text-3xl font-light mt-2">
            Secure Sign In
          </h1>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
            Review and manage confidential inquiries from the public site.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
            <div>
              <label className="overline block mb-3" htmlFor="admin-email">
                Email
              </label>
              <input
                id="admin-email"
                data-testid="admin-login-email"
                type="email"
                autoComplete="username"
                className="input-luxe"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@thebridgecollective.com"
              />
            </div>
            <div>
              <label className="overline block mb-3" htmlFor="admin-password">
                Password
              </label>
              <input
                id="admin-password"
                data-testid="admin-login-password"
                type="password"
                autoComplete="current-password"
                className="input-luxe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="admin-login-submit"
              className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {submitting ? "Signing in..." : "Sign In"}
              <ArrowUpRight size={16} strokeWidth={1.5} />
            </button>

            <div className="flex items-start gap-3 text-xs text-zinc-500 pt-2">
              <Lock size={14} className="text-[#1A5C40] mt-0.5" strokeWidth={1.4} />
              <span>
                Sessions expire automatically. Five failed attempts trigger a
                temporary lockout.
              </span>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
